import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { isFunction } from 'lodash';
import { currentHospital } from '../../../store/reducers/ward';
import { setSnackbar } from '../../../store/reducers/globals';
import { agylePref } from '../../../store/reducers/preferences';
import { useCentralPageStyles } from '../centralPageStyles';
import { Grid, Icon } from '@mui/material';
import { AgyleNamedIcon } from '../../../components/icons/agyle';
import { MODULE_NAMES } from '../constants';
import { clearSearchFilter, setTreatmentsBoardOpened } from '../../../store/reducers/patientContext';
import TruncatedText from '../../../components/ui/text/truncatedText';
import Typography from '@mui/material/Typography';

const CentralPageModuleBox = ({ module, tileSize, paperBackground, isCentralPage }) => {
    const { id, name, enabled, iconName, disabled, description, openNewCard, componentPath, cornerIcon } = module;

    const history = useHistory();
    const { t } = useTranslation();
    const location = useLocation();
    const dispatch = useDispatch();
    const classes = useCentralPageStyles({ size: tileSize, paperBackground, groupColor: module.groupColor });

    const currHospitalId = useSelector(currentHospital);
    const agylePreferences = useSelector(agylePref);

    const handleFunctionalRedirect = useCallback(() => {
        switch (name) {
            case MODULE_NAMES.ESTIMATED_WAITING_TIME:
                if (!currHospitalId && !agylePreferences?.customMap) {
                    return dispatch(setSnackbar({ type: 'error', message: 'Please go to whiteboard view and choose your hospital.' }));
                } else if (currHospitalId) {
                    return history.push(componentPath(currHospitalId));
                } else {
                    return history.push(componentPath(agylePreferences?.customMap?.hospitalId));
                }
            default:
                return dispatch(
                    setSnackbar({ type: 'error', message: 'Something went wrong. Please contact support and provide details.' }),
                );
        }
    }, [name, currHospitalId, dispatch]);

    const handleRedirect = useCallback(() => {
        if (componentPath && enabled && !disabled) {
            dispatch(setTreatmentsBoardOpened(null));
            if (openNewCard) {
                window.open(componentPath.toString(), '_blank', 'noopener,noreferrer');
            } else if (
                id === 'wardView' &&
                agylePreferences?.wardMap?.defaultMapEnabled &&
                agylePreferences?.wardMap?.defaultMap &&
                isCentralPage
            ) {
                return history.push(`/ward/${agylePreferences?.wardMap?.defaultMap}`);
            } else if (isFunction(componentPath)) {
                return handleFunctionalRedirect();
            } else {
                if (module?.name === MODULE_NAMES.SEARCH) {
                    dispatch(clearSearchFilter());
                }
                return history.push(componentPath);
            }
        } else {
            return dispatch(setSnackbar({ type: 'error', message: 'You do not have permission to access.' }));
        }
    }, [
        componentPath,
        handleFunctionalRedirect,
        agylePreferences?.wardMap?.defaultMapEnabled,
        agylePreferences?.wardMap?.defaultMap,
        isCentralPage,
    ]);

    const isLocationIncludesPath = useMemo(() => {
        if (location && location?.pathname) {
            return location.pathname.includes(componentPath);
        }
    }, [location?.pathname]);

    return (
        <Grid
            item
            onClick={handleRedirect.bind(this, componentPath)}
            className={clsx(classes.moduleBoxRoot, !enabled && 'disabledRedirect', isLocationIncludesPath && 'currentLocation')}
        >
            {cornerIcon && (
                <div className={classes.moduleCornerIcon}>
                    <AgyleNamedIcon name={cornerIcon} htmlColor={module.groupColor != null ? module.groupColor : undefined} />
                </div>
            )}
            <Icon className={classes.moduleBoxIcon}>
                <AgyleNamedIcon name={iconName || 'agyleIcon'} htmlColor={module.groupColor != null ? module.groupColor : undefined} />
            </Icon>
            <Typography className={classes.moduleBoxDescription}>{t(description)}</Typography>
            {/*<TruncatedText text={t(description)} className={classes.moduleBoxDescription} fw={700} textAlign="center" />       */}
            {/*<Typography textAlign='center' fontWeight={700} className={classes.moduleBoxDescription}>*/}
            {/*  {t(description)}*/}
            {/*</Typography>*/}
        </Grid>
    );
};

CentralPageModuleBox.propTypes = {
    module: PropTypes.object.isRequired,
    isCentralPage: PropTypes.bool,
    paperBackground: PropTypes.bool,
    tileSize: PropTypes.oneOf(['small', 'medium', 'large']),
};

CentralPageModuleBox.defaultProps = {
    tileSize: 'large',
    isCentralPage: false,
    paperBackground: false,
};

export default CentralPageModuleBox;
